import React, { MouseEvent, memo, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Container, Typography } from '@cleartrip/ct-design-components'

import {
    CabinMediaDrawerArrowLeft,
    CabinMediaDrawerArrowRight,
    CloseIcon,
} from '@/assets/icons'

import CabinMediaSlide from './CabinMediaSlide'
import {
    CABIN_MEDIA_COPY_MAX_WIDTH_PX,
    CABIN_MEDIA_DRAWER_BOTTOM_GRADIENT,
    CABIN_MEDIA_DRAWER_CLOSE_BUTTON_GAP_PX,
    CABIN_MEDIA_DRAWER_CLOSE_BUTTON_OFFSET_TOP_PX,
    CABIN_MEDIA_DRAWER_CLOSE_BUTTON_SIZE_PX,
    CABIN_MEDIA_DRAWER_CLOSE_ICON_SIZE_PX,
    CABIN_MEDIA_DRAWER_OVERLAY_BG,
    CABIN_MEDIA_DRAWER_PANEL_MAX_WIDTH_PX,
    CABIN_MEDIA_DRAWER_TOP_GRADIENT,
    CABIN_MEDIA_DRAWER_VERTICAL_MARGIN_PX,
    CABIN_MEDIA_DRAWER_Z_INDEX,
    CABIN_MEDIA_PROGRESS_BAR_HEIGHT_PX,
    CABIN_MEDIA_PROGRESS_GAP_PX,
} from './constants'
import { toCabinMediaItems } from './normalizeCabinMedia'
import { CabinMediaDrawerProps } from './types'
import useCabinMediaDrawer from './useCabinMediaDrawer'
import useCabinMediaPanelSize from './useCabinMediaPanelSize'

const NAV_BUTTON_SIZE_PX = 56
const TAP_ZONE_WIDTH_PERCENT = 30

type NavButtonProps = {
    direction: 'left' | 'right'
    onClick: () => void
    disabled?: boolean
}

const NavButton = ({ direction, onClick, disabled }: NavButtonProps) => {
    const Icon = direction === 'left' ? CabinMediaDrawerArrowLeft : CabinMediaDrawerArrowRight

    return (
        <button
            type="button"
            aria-label={direction === 'left' ? 'Previous' : 'Next'}
            className="border-none bg-transparent p-0 c-pointer"
            style={{
                width: NAV_BUTTON_SIZE_PX,
                height: NAV_BUTTON_SIZE_PX,
                flexShrink: 0,
                opacity: disabled ? 0.4 : 1,
                cursor: disabled ? 'default' : 'pointer',
            }}
            disabled={disabled}
            onClick={onClick}
        >
            <Icon width={NAV_BUTTON_SIZE_PX} height={NAV_BUTTON_SIZE_PX} />
        </button>
    )
}

const CabinMediaDrawer = ({
    isOpen,
    onClose,
    resources,
    initialIndex = 0,
    allowBackdropClose = true,
}: CabinMediaDrawerProps) => {
    const items = useMemo(() => toCabinMediaItems(resources), [resources])
    const itemCount = items.length
    const panelSize = useCabinMediaPanelSize(isOpen)

    const copyMaxWidth = panelSize.width * (CABIN_MEDIA_COPY_MAX_WIDTH_PX / CABIN_MEDIA_DRAWER_PANEL_MAX_WIDTH_PX)

    const {
        activeIndex,
        canGoPrevious,
        showNavigation,
        goToPrevious,
        goToNext,
        handlePlaybackProgress,
        handleMediaComplete,
        setSegmentFillRef,
    } = useCabinMediaDrawer({
        isOpen,
        itemCount,
        initialIndex,
        onClose,
    })

    const activeItem = items[activeIndex]

    useEffect(() => {
        if (!isOpen) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isOpen])

    const handleOverlayClick = useCallback(() => {
        if (allowBackdropClose) {
            onClose()
        }
    }, [allowBackdropClose, onClose])

    const handlePanelClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
    }, [])

    const handleTapZoneClick = useCallback(
        (direction: 'previous' | 'next') => (event: MouseEvent<HTMLDivElement>) => {
            event.stopPropagation()
            if (direction === 'previous') {
                goToPrevious()
                return
            }
            goToNext()
        },
        [goToNext, goToPrevious]
    )

    if (!isOpen || !itemCount || typeof document === 'undefined') return null

    return createPortal(
        <Container
            className="p-fixed flex flex-center flex-middle"
            css={{
                inset: 0,
                zIndex: CABIN_MEDIA_DRAWER_Z_INDEX,
                background: CABIN_MEDIA_DRAWER_OVERLAY_BG,
                padding: `${CABIN_MEDIA_DRAWER_VERTICAL_MARGIN_PX}px 0`,
            }}
            onClick={handleOverlayClick}
        >
            <Container
                className="flex flex-middle flex-center"
                css={{ gap: '24px' }}
                onClick={handlePanelClick}
            >
                {showNavigation && (
                    <NavButton direction="left" onClick={goToPrevious} disabled={!canGoPrevious} />
                )}

                <Container
                    className="p-relative"
                    css={{ flexShrink: 0 }}
                >
                    <Container
                        className="p-relative o-hidden"
                        css={{
                            width: `${panelSize.width}px`,
                            height: `${panelSize.height}px`,
                            borderRadius: `${panelSize.borderRadius}px`,
                            backgroundColor: '#000000',
                        }}
                    >
                    <Container className="p-absolute t-0 l-0 h-100p w-100p" css={{ zIndex: 1 }}>
                        {activeItem && (
                            <CabinMediaSlide
                                item={activeItem}
                                isActive={isOpen}
                                onPlaybackProgress={handlePlaybackProgress}
                                onMediaComplete={handleMediaComplete}
                            />
                        )}
                    </Container>

                    <Container
                        className="p-absolute w-100p"
                        top={0}
                        height="118px"
                        css={{
                            background: CABIN_MEDIA_DRAWER_TOP_GRADIENT,
                            zIndex: 2,
                            pointerEvents: 'none',
                        }}
                    />

                    <Container
                        className="p-absolute w-100p"
                        bottom={0}
                        height="200px"
                        css={{
                            background: CABIN_MEDIA_DRAWER_BOTTOM_GRADIENT,
                            zIndex: 2,
                            pointerEvents: 'none',
                        }}
                    />

                    {showNavigation && (
                        <Container
                            className="p-absolute flex"
                            top={24}
                            left={20}
                            right={20}
                            css={{
                                gap: `${CABIN_MEDIA_PROGRESS_GAP_PX}px`,
                                zIndex: 3,
                            }}
                        >
                            {items.map((item, index) => (
                                <Container
                                    key={item.id}
                                    className="flex-1 o-hidden br-pill"
                                    height={`${CABIN_MEDIA_PROGRESS_BAR_HEIGHT_PX}px`}
                                    backgroundColor="rgba(255, 255, 255, 0.3)"
                                >
                                    <Container
                                        ref={setSegmentFillRef(index)}
                                        height="100%"
                                        width="100%"
                                        backgroundColor="#FFFFFF"
                                        css={{
                                            borderRadius: '48px',
                                            transformOrigin: 'left center',
                                            willChange: 'transform',
                                        }}
                                    />
                                </Container>
                            ))}
                        </Container>
                    )}

                    {(activeItem?.title || activeItem?.description) && (
                        <Container
                            className="p-absolute flex flex-column"
                            bottom={16}
                            left={20}
                            css={{
                                gap: '8px',
                                zIndex: 3,
                                maxWidth: `${copyMaxWidth}px`,
                                pointerEvents: 'none',
                            }}
                        >
                            {activeItem.title && (
                                <Typography
                                    variant="HM1"
                                    className="c-neutral-100"
                                    css={{
                                        textShadow: '1px 2px 2px rgba(26, 26, 26, 0.3)',
                                    }}
                                >
                                    {activeItem.title}
                                </Typography>
                            )}
                            {activeItem.description && (
                                <Typography
                                    variant="P1"
                                    css={{
                                        color: '#B3B3B3',
                                        textShadow: '1px 1px 2px rgba(26, 26, 26, 0.24)',
                                    }}
                                >
                                    {activeItem.description}
                                </Typography>
                            )}
                        </Container>
                    )}

                    {showNavigation && (
                        <>
                            <Container
                                className="p-absolute"
                                top={0}
                                bottom={0}
                                left={0}
                                css={{
                                    width: `${TAP_ZONE_WIDTH_PERCENT}%`,
                                    zIndex: 4,
                                }}
                                onClick={handleTapZoneClick('previous')}
                            />
                            <Container
                                className="p-absolute"
                                top={0}
                                bottom={0}
                                right={0}
                                css={{
                                    width: `${TAP_ZONE_WIDTH_PERCENT}%`,
                                    zIndex: 4,
                                }}
                                onClick={handleTapZoneClick('next')}
                            />
                        </>
                    )}

                    </Container>

                    <button
                        type="button"
                        aria-label="Close gallery"
                        className="p-absolute border-none bg-white c-pointer flex flex-center flex-middle"
                        style={{
                            top: `${CABIN_MEDIA_DRAWER_CLOSE_BUTTON_OFFSET_TOP_PX}px`,
                            right: `-${CABIN_MEDIA_DRAWER_CLOSE_BUTTON_GAP_PX + CABIN_MEDIA_DRAWER_CLOSE_BUTTON_SIZE_PX}px`,
                            width: `${CABIN_MEDIA_DRAWER_CLOSE_BUTTON_SIZE_PX}px`,
                            height: `${CABIN_MEDIA_DRAWER_CLOSE_BUTTON_SIZE_PX}px`,
                            borderRadius: '40px',
                            padding: '4px',
                            zIndex: 6,
                        }}
                        onClick={onClose}
                    >
                        <CloseIcon
                            width={CABIN_MEDIA_DRAWER_CLOSE_ICON_SIZE_PX}
                            height={CABIN_MEDIA_DRAWER_CLOSE_ICON_SIZE_PX}
                        />
                    </button>
                </Container>

                {showNavigation && <NavButton direction="right" onClick={goToNext} />}
            </Container>
        </Container>,
        document.body
    )
}

export default memo(CabinMediaDrawer)
